import { ALERT_SEVERITY } from "Util/alerts";
import { KEYS, NAMESPACE } from "ESignatures/i18n/eSignatureConstants.i18n";

export const ESIGNATURE_OPTIONS = {
    None: 1,
    Required: 2,
    Optional: 3
};

export const SIGNATORY_CHOICE_CONSTANTS = {
    FirstNameDataFields: "FirstNameDataFields",
    LastNameDataFields: "LastNameDataFields",
    EmailDataFields: "EmailDataFields",
    UserDataFields: "UserDataFields"
};

/** Parallels PC2.ESignatureProviders.Models.EnvelopeStatus */
export const ENVELOPE_STATUS = {
    New: 1,
    Sent: 2,
    Delivered: 3,
    Completed: 4,
    Declined: 5,
    Voided: 6
};

/** Parallels PC2.ESignatureProviders.Models.RecipientStatus */
export const ENVELOPE_RECIPIENT_STATUS = {
    Created: 1,
    Sent: 2,
    Delivered: 3,
    Signed: 4,
    Declined: 5,
    Completed: 6,
    FaxPending: 7,
    AutoResponded: 8
};

export const ESIGNATURE_STATUS_SEVERITY = {
    Info: 1,
    Success: 2,
    Error: 3,
    Warning: 4
};

const ESIGNATURE_STATUS_TEXT_BUBBLE_CLASS = {
    [ESIGNATURE_STATUS_SEVERITY.Info]: "text-bubble-info",
    [ESIGNATURE_STATUS_SEVERITY.Success]: "text-bubble-success",
    [ESIGNATURE_STATUS_SEVERITY.Error]: "text-bubble-danger",
    [ESIGNATURE_STATUS_SEVERITY.Warning]: "text-bubble-warning"
};

const ESIGNATURE_STATUS_TEXT_CLASS = {
    [ESIGNATURE_STATUS_SEVERITY.Info]: "text-info",
    [ESIGNATURE_STATUS_SEVERITY.Success]: "text-success",
    [ESIGNATURE_STATUS_SEVERITY.Error]: "text-danger",
    [ESIGNATURE_STATUS_SEVERITY.Warning]: "text-warning"
};

const ESIGNATURE_STATUS_ALERT_SEVERITY = {
    [ESIGNATURE_STATUS_SEVERITY.Info]: ALERT_SEVERITY.info,
    [ESIGNATURE_STATUS_SEVERITY.Success]: ALERT_SEVERITY.success,
    [ESIGNATURE_STATUS_SEVERITY.Error]: ALERT_SEVERITY.error,
    [ESIGNATURE_STATUS_SEVERITY.Warning]: ALERT_SEVERITY.warning
};

export function getEnvelopeStatusTextBubbleClass(statusCode) {
    return ESIGNATURE_STATUS_TEXT_BUBBLE_CLASS[getEnvelopeStatusSeverity(statusCode)];
}

export function getEnvelopeStatusAlertSeverity(statusCode) {
    return ESIGNATURE_STATUS_ALERT_SEVERITY[getEnvelopeStatusSeverity(statusCode)];
}

export function getEnvelopeStatusTextClass(statusCode) {
    return ESIGNATURE_STATUS_TEXT_CLASS[getEnvelopeStatusSeverity(statusCode)];
}

export function getRecipientStatusTextBubbleClass(statusCode) {
    return ESIGNATURE_STATUS_TEXT_BUBBLE_CLASS[getRecipientStatusSeverity(statusCode)];
}

export function getRecipientStatusAlertSeverity(statusCode) {
    return ESIGNATURE_STATUS_ALERT_SEVERITY[getRecipientStatusSeverity(statusCode)];
}

export function getRecipientStatusTextClass(statusCode) {
    return ESIGNATURE_STATUS_TEXT_CLASS[getRecipientStatusSeverity(statusCode)];
}

export function getESignatureStatusTextClassBySeverity(severity) {
    return ESIGNATURE_STATUS_TEXT_CLASS[severity];
}

export function getESignatureStatusTextBubbleClassBySeverity(severity) {
    return ESIGNATURE_STATUS_TEXT_BUBBLE_CLASS[severity];
}

export function getEnvelopeStatusSeverity(statusCode) {
    switch (statusCode) {
        case ENVELOPE_STATUS.New:
        case ENVELOPE_STATUS.Sent:
        case ENVELOPE_STATUS.Delivered:
            return ESIGNATURE_STATUS_SEVERITY.Info;
        case ENVELOPE_STATUS.Completed:
            return ESIGNATURE_STATUS_SEVERITY.Success;
        case ENVELOPE_STATUS.Declined:
        case ENVELOPE_STATUS.Voided:
            return ESIGNATURE_STATUS_SEVERITY.Error;
    }
}

export function getRecipientStatusSeverity(statusCode) {
    switch (statusCode) {
        case ENVELOPE_RECIPIENT_STATUS.Created:
        case ENVELOPE_RECIPIENT_STATUS.Sent:
        case ENVELOPE_RECIPIENT_STATUS.Delivered:
        case ENVELOPE_RECIPIENT_STATUS.FaxPending:
            return ESIGNATURE_STATUS_SEVERITY.Info;
        case ENVELOPE_RECIPIENT_STATUS.Signed:
        case ENVELOPE_RECIPIENT_STATUS.Completed:
            return ESIGNATURE_STATUS_SEVERITY.Success;
        case ENVELOPE_RECIPIENT_STATUS.Declined:
        case ENVELOPE_RECIPIENT_STATUS.AutoResponded:
            return ESIGNATURE_STATUS_SEVERITY.Error;
    }
}

const TERMINAL_RECIPIENT_STATUSES = new Set([ENVELOPE_RECIPIENT_STATUS.Declined, ENVELOPE_RECIPIENT_STATUS.Completed]);
export function isRecipientStatusTerminal(statusCode) {
    return TERMINAL_RECIPIENT_STATUSES.has(statusCode);
}

const TERMINAL_ENVELOPE_STATUSES = new Set([
    ENVELOPE_STATUS.Completed,
    ENVELOPE_STATUS.Declined,
    ENVELOPE_STATUS.Voided
]);
export function isEnvelopeStatusTerminal(statusCode) {
    return TERMINAL_ENVELOPE_STATUSES.has(statusCode);
}

export function isEnvelopeStatusFailed(statusCode) {
    return statusCode === ENVELOPE_STATUS.Declined || statusCode === ENVELOPE_STATUS.Voided;
}

/**
 * Corresponds to Common.BusinessLogic.ESignatures.ESignatureCancelExplanationType
 */
export const ESIGNATURE_CANCEL_EXPLANATION_TYPE = {
    Other: 0,
    ResendingNotification: 1,
    CorrectingRecipientContactInfo: 2,
    UpdatingContents: 3,
    CpAdminCancellation: 4,
    WetSign: 5
};

const eSignatureCancelResourceKeyByType = {
    [ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other]: KEYS.cancelExplanationTypeOtherLabel,
    [ESIGNATURE_CANCEL_EXPLANATION_TYPE.ResendingNotification]: KEYS.cancelExplanationTypeResendingLabel,
    [ESIGNATURE_CANCEL_EXPLANATION_TYPE.CorrectingRecipientContactInfo]:
        KEYS.cancelExplanationTypeCorrectingRecipientLabel,
    [ESIGNATURE_CANCEL_EXPLANATION_TYPE.UpdatingContents]: KEYS.cancelExplanationTypeUpdatingContentsLabel,
    [ESIGNATURE_CANCEL_EXPLANATION_TYPE.CpAdminCancellation]: KEYS.cancelExplanationTypeCpAdminLabel
};

export const WET_SIGN_VOID_REASON = "Wet sign discard";

export function getESignatureCancelExplanationLabel($app, cancelExplanationType) {
    const key = eSignatureCancelResourceKeyByType[cancelExplanationType];
    if (!key) {
        throw `Missing mapping for cancel explanation type ${cancelExplanationType} to label key`;
    }

    return $app.res({
        namespace: NAMESPACE,
        key
    });
}

export function getEnvelopeSignatureTypeLabel($app, envelopeIsWetSign) {
    const key = envelopeIsWetSign ? KEYS.signatureTypeWetSign : KEYS.signatureTypeESign;
    return $app.res({
        namespace: NAMESPACE,
        key
    });
}

export function isEnvelopeInStatus(clientEnvelope, status) {
    return clientEnvelope.Status.Code === status;
}
